import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import MainContainer from "../../components/partials/maincontainer";
import { IconButton } from "../../components/partials/btn";
import { InputWithIcon } from "../../components/partials/inputs";
import PrimaryBtn from "../../components/partials/btn";
import { Controller, useForm } from "react-hook-form";
import { useApiCall } from "../../data/useFetcher";
import { useUsersStore } from "../../data/stores/loggerStore";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";

interface FormType {
  departmentName: string;
  description: string;
  departmentHead: string;
  departmentCode: string;
  organizationId: string;
  email: string;
  phone: string;
  location: string;
  budget: string;
  establishedDate: string;
}

const AddDepartment = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      departmentName: "",
      description: "",
      departmentHead: "",
      departmentCode: "",
      organizationId: "",
      email: "",
      phone: "",
      location: "",
      budget: "",
      establishedDate: "",
    },
  });

  const { onSubmitAPI } = useApiCall();
  const { getDynamicLogger } = useUsersStore();

  const onSubmit = async (data: FormType) => {
    setIsSubmitting(true);
    try {
      await onSubmitAPI({
        url: `/api/v1/department/create`,
        type: "post",
        data: data,
        toggleModal: () => {
          toast.success("Department created successfully!");
          navigate("/department");
        },
      });
    } catch (error) {
      toast.error("Failed to create department. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/department");
  };

  return (
    <div>
      <MainContainer>
        <div className="bg-transparent main-border main-shadow rounded-lg">
          {/* Header */}
          <div
            style={{
              borderBottom: "1px solid #D7E2EC",
            }}
            className="h-24 w-full px-5 flex justify-between items-center"
          >
            <div>
              <div className="flex gap-2 items-center">
                <button
                  onClick={() => navigate("/department")}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Icon
                    icon="material-symbols:arrow-back"
                    className="text-gray-600"
                  />
                </button>
                <h4 className="text-lg font-medium text-sec">
                  Add New Department
                </h4>
              </div>
              <p className="text-sm text-es-gray-100 mt-1 font-normal">
                Create a new department in your organization
              </p>
            </div>
            <div className="flex gap-3">
              <IconButton
                onClick={handleCancel}
                icon="material-symbols:close"
                text="Cancel"
                bg="transparent"
                color="#6B7280"
              />
            </div>
          </div>

          {/* Form */}
          <div className="p-6">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Basic Information Section */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="text-md font-medium text-gray-800 mb-4 flex items-center gap-2">
                  <Icon
                    icon="material-symbols:info"
                    className="text-blue-600"
                  />
                  Basic Information
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Controller
                      name="departmentName"
                      control={control}
                      rules={{
                        required: "Department name is required",
                        minLength: {
                          value: 2,
                          message:
                            "Department name must be at least 2 characters",
                        },
                      }}
                      render={({ field: { value, onChange, name } }) => (
                        <InputWithIcon
                          icon="material-symbols:business"
                          placeholder="e.g., Human Resources"
                          label="Department Name *"
                          value={value}
                          onChange={onChange}
                          name={name}
                        />
                      )}
                    />
                    {errors.departmentName && (
                      <p className="text-[#dc2626] text-xs mt-1">
                        {errors.departmentName.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Controller
                      name="departmentCode"
                      control={control}
                      rules={{
                        required: "Department code is required",
                        pattern: {
                          value: /^[A-Z0-9]{2,10}$/,
                          message:
                            "Department code must be 2-10 uppercase letters/numbers",
                        },
                      }}
                      render={({ field: { value, onChange, name } }) => (
                        <InputWithIcon
                          icon="material-symbols:tag"
                          placeholder="e.g., HR, IT, FIN"
                          label="Department Code *"
                          value={value}
                          onChange={onChange}
                          name={name}
                        />
                      )}
                    />
                    {errors.departmentCode && (
                      <p className="text-[#dc2626] text-xs mt-1">
                        {errors.departmentCode.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-4">
                  <Controller
                    name="description"
                    control={control}
                    rules={{
                      required: "Description is required",
                      minLength: {
                        value: 10,
                        message: "Description must be at least 10 characters",
                      },
                    }}
                    render={({ field: { value, onChange, name } }) => (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Description *
                        </label>
                        <textarea
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                          placeholder="Brief description of the department's role and responsibilities..."
                          value={value}
                          onChange={onChange}
                          name={name}
                          rows={3}
                        />
                      </div>
                    )}
                  />
                  {errors.description && (
                    <p className="text-[#dc2626] text-xs mt-1">
                      {errors.description.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Leadership Section */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="text-md font-medium text-gray-800 mb-4 flex items-center gap-2">
                  <Icon
                    icon="material-symbols:person"
                    className="text-green-600"
                  />
                  Leadership
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Controller
                      name="departmentHead"
                      control={control}
                      rules={{
                        required: "Department head is required",
                      }}
                      render={({ field: { value, onChange, name } }) => (
                        <InputWithIcon
                          icon="material-symbols:person"
                          placeholder="e.g., John Doe"
                          label="Department Head *"
                          value={value}
                          onChange={onChange}
                          name={name}
                        />
                      )}
                    />
                    {errors.departmentHead && (
                      <p className="text-[#dc2626] text-xs mt-1">
                        {errors.departmentHead.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Controller
                      name="email"
                      control={control}
                      rules={{
                        required: "Email is required",
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: "Invalid email format",
                        },
                      }}
                      render={({ field: { value, onChange, name } }) => (
                        <InputWithIcon
                          icon="material-symbols:email"
                          placeholder="e.g., john.doe@company.com"
                          label="Head Email *"
                          type="email"
                          value={value}
                          onChange={onChange}
                          name={name}
                        />
                      )}
                    />
                    {errors.email && (
                      <p className="text-[#dc2626] text-xs mt-1">
                        {errors.email.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Additional Information Section */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="text-md font-medium text-gray-800 mb-4 flex items-center gap-2">
                  <Icon
                    icon="material-symbols:settings"
                    className="text-purple-600"
                  />
                  Additional Information
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Controller
                      name="phone"
                      control={control}
                      render={({ field: { value, onChange, name } }) => (
                        <InputWithIcon
                          icon="material-symbols:phone"
                          placeholder="e.g., +1 (555) 123-4567"
                          label="Phone Number"
                          value={value}
                          onChange={onChange}
                          name={name}
                        />
                      )}
                    />
                  </div>

                  <div>
                    <Controller
                      name="location"
                      control={control}
                      render={({ field: { value, onChange, name } }) => (
                        <InputWithIcon
                          icon="material-symbols:location-on"
                          placeholder="e.g., Building A, Floor 2"
                          label="Location"
                          value={value}
                          onChange={onChange}
                          name={name}
                        />
                      )}
                    />
                  </div>

                  <div>
                    <Controller
                      name="budget"
                      control={control}
                      render={({ field: { value, onChange, name } }) => (
                        <InputWithIcon
                          icon="material-symbols:attach-money"
                          placeholder="e.g., $100,000"
                          label="Annual Budget"
                          value={value}
                          onChange={onChange}
                          name={name}
                        />
                      )}
                    />
                  </div>

                  <div>
                    <Controller
                      name="establishedDate"
                      control={control}
                      render={({ field: { value, onChange, name } }) => (
                        <InputWithIcon
                          icon="material-symbols:calendar-today"
                          placeholder="e.g., 2024-01-01"
                          label="Established Date"
                          type="date"
                          value={value}
                          onChange={onChange}
                          name={name}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <PrimaryBtn
                  text="Create Department"
                  type="submit"
                  loading={isSubmitting}
                  bg="#2070B8"
                  color="white"
                />
              </div>
            </form>
          </div>
        </div>
      </MainContainer>
    </div>
  );
};

export default AddDepartment;
